"use client"

import { createContext, useContext, ReactNode, useCallback } from "react"

import { useAuthToken, setSessionAuthToken } from "@lib/hooks/use-auth-token"
import { getAuthState, clearAuthCookie } from "@lib/util/auth"

interface AuthContextType {
  authToken: string | null
  setAuthToken: (token: string) => void
  clearAuthToken: () => void
  isAuthenticated: boolean
  isExpired: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children, initialToken }: { children: ReactNode; initialToken?: string | null }) => {
  const authToken = useAuthToken(initialToken || undefined)
  const authState = getAuthState(authToken)

  const setAuthToken = useCallback((token: string) => {
    setSessionAuthToken(token)
  }, [])

  const clearAuthToken = useCallback(() => {
    clearAuthCookie()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        authToken,
        setAuthToken,
        clearAuthToken,
        isAuthenticated: authState.isAuthenticated,
        isExpired: authState.isExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}
